/* istanbul ignore file */
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useApi, discoveryApiRef } from "@backstage/core-plugin-api";

interface StanConfig {
  displayName: string;
}

interface StanConfigContextType {
  config: StanConfig | null;
  loading: boolean;
  error: Error | null;
}

const StanConfigContext = createContext<StanConfigContextType | undefined>(
  undefined
);

export const useStanConfig = (): StanConfigContextType => {
  const context = useContext(StanConfigContext);
  if (!context) {
    throw new Error("useStanConfig must be used within StanConfigProvider");
  }
  return context;
};

export const StanConfigProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const discoveryApi = useApi(discoveryApiRef);
  const [config, setConfig] = useState<StanConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl("stan");
        const response = await fetch(`${baseUrl}/config`);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch STAN config: ${response.statusText}`
          );
        }
        const data = await response.json();
        setConfig(data as StanConfig);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        // Set default config on error
        setConfig({ displayName: "STAN" });
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [discoveryApi]);

  return (
    <StanConfigContext.Provider value={{ config, loading, error }}>
      {children}
    </StanConfigContext.Provider>
  );
};
